<template>
  <VCarousel
    v-model="slider"
    :show-arrows="false"
    :hide-delimiters="hideControls"
    hide-delimiter-background
    light
    height="unset"
  >
    <VCarouselItem
      v-for="(productChunk, index) in productsSliced"
      :key="index"
    >
      <VRow no-gutters class="img-grid mx-n2" :justify="shouldCenter ? 'center' : 'start'">
        <VCol v-for="(product, i) in productChunk" :key="i" :cols="colSize" class="pa-0">
          <ProductCard
            :product="product"
            :source="source"
            :hide-wishlist="hideWishlist"
            :sizes="sizes"
            show-add-button
            :truncate-title="2"
            class="pb-12"
            @clicked-product="productClicked(product)"
          />
        </VCol>
      </VRow>
    </VCarouselItem>
  </VCarousel>
</template>

<script lang="ts" setup>
import ProductCard from '~/components/categories/ProductCard.vue'
import { CAROUSEL_CARD_SIZES } from '~/utils/format/imageSizes'
import type { ListedProduct, ListedProduct as Product } from '~/types/shop'
import { pushSelectItem, pushViewItemList } from '~/utils/gtm'
import { useLocaleStore } from '~/stores/locale'
import type { AddToCartSource } from '~/stores/cart'

interface Props {
  background?: string
  products?: Product[]
  hideWishlist?: boolean
  cols?: '2' | '3' | '4' | '6'
  /** The CSS slot a tile fills; defaults to this carousel's own grid. */
  sizes?: string
  source?: AddToCartSource
}

const props = withDefaults(defineProps<Props>(), {
  background: 'white',
  products: () => [],
  hideWishlist: false,
  cols: undefined,
  sizes: CAROUSEL_CARD_SIZES,
  source: undefined
})

const emits = defineEmits<{
  (e: 'clicked-product', product: ListedProduct, position: number): void
}>()

const localeStore = useLocaleStore()

const slider = ref<number>()

const {proxy:gtm} = useScriptGoogleTagManager()
onMounted(() => pushViewItemList(gtm, props.products, localeStore.currency))


const { breakpoints } = useBreakpoints()

const chunkSize = computed(() => {
  return breakpoints.xs.value ? 2 : breakpoints.sm.value ? 3 : breakpoints.md.value ? 4 : 6
})

const productsSliced = computed(() => {
  let products = breakpoints.xs.value ? props.products.slice(0, 8) : props.products
  const partialChunk = products.length % chunkSize.value
  if (partialChunk > 0 && products.length > chunkSize.value) {
    products = [...products, ...products.slice(0, chunkSize.value - partialChunk)]
  }

  const items = Array.from({ length: Math.ceil(products.length / chunkSize.value) }, (_, i) =>
    products.slice(i * chunkSize.value, i * chunkSize.value + chunkSize.value)
  )

  return items
})

// Hide carousel controls (delimiters/dots) if there's only one page
const hideControls = computed(() => productsSliced.value.length <= 1)

// Center products if there are fewer products than the chunk size
const shouldCenter = computed(() => props.products.length < chunkSize.value && props.products.length > 0)

const colSize = computed(() => {
  if (props.cols) {
    return props.cols
  }

  return breakpoints.xs.value ? 6 : breakpoints.sm.value ? 4 : breakpoints.md.value ? 3 : 2
})

// productsSliced pads the final chunk with repeats of earlier products, so neither the carousel
// page index nor the position within a chunk describes rank. The product's place in the source
// list does.
const productClicked = (product: ListedProduct) => {
  const position = props.products.findIndex(p => p.id === product.id)
  // -1 means the click did not come from a card this carousel rendered — a miswire, not a
  // rank. Reporting it would arrive downstream as position 0 after the `+ 1` in
  // trackSearchResultClick, indistinguishable from a legitimate top result, so a silent
  // wiring bug would quietly improve the mean-click-position metric. AB#63268
  if (position < 0) {
    return
  }
  pushSelectItem(gtm, product, position)
  emits('clicked-product', product, position)
}
</script>

<style lang='scss' scoped>
.arrow-btn {
  cursor: pointer;
  z-index: 9;
  &.left {
    margin-right: 32px;
  }
  &.right {
    margin-left: 32px;
  }
  img {
    object-fit: none;
  }
}
</style>
